import React, { useState } from "react";
import { TanTableClient } from "components/molecules";
import { TanTableColumn } from "components/molecules";
import { PaziaButton, PaziaLoader } from "components/atoms";
import { useToaster } from "utils";
import {
  ksefApi,
  useKsefSubmissions,
  useKsefSubmission,
  KSeFSubmissionDto,
  KSeFSubmissionDetailDto,
  KSeFSubmissionStatus,
} from "api/ksef";
import { PaziaModal } from "components/molecules/Modals";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";

const statusLabel: Record<KSeFSubmissionStatus, string> = {
  [KSeFSubmissionStatus.Pending]: "Oczekuje",
  [KSeFSubmissionStatus.Submitted]: "Wysłano",
  [KSeFSubmissionStatus.Accepted]: "Zaakceptowano",
  [KSeFSubmissionStatus.Rejected]: "Odrzucono",
  [KSeFSubmissionStatus.Error]: "Błąd",
  [KSeFSubmissionStatus.RetryScheduled]: "Ponowna próba",
};

const statusColor: Record<KSeFSubmissionStatus, string> = {
  [KSeFSubmissionStatus.Pending]: "bg-yellow-100 text-yellow-800",
  [KSeFSubmissionStatus.Submitted]: "bg-blue-100 text-blue-800",
  [KSeFSubmissionStatus.Accepted]: "bg-green-100 text-green-800",
  [KSeFSubmissionStatus.Rejected]: "bg-red-100 text-red-800",
  [KSeFSubmissionStatus.Error]: "bg-red-100 text-red-800",
  [KSeFSubmissionStatus.RetryScheduled]: "bg-orange-100 text-orange-800",
};

const statusFilters: { label: string; value?: KSeFSubmissionStatus }[] = [
  { label: "Wszystkie" },
  { label: "Oczekuje", value: KSeFSubmissionStatus.Pending },
  { label: "Wysłano", value: KSeFSubmissionStatus.Submitted },
  { label: "Zaakceptowano", value: KSeFSubmissionStatus.Accepted },
  { label: "Odrzucono", value: KSeFSubmissionStatus.Rejected },
  { label: "Błąd", value: KSeFSubmissionStatus.Error },
  { label: "Ponowna próba", value: KSeFSubmissionStatus.RetryScheduled },
];

interface SubmissionsTabProps {
  integrationId: number;
}

export const SubmissionsTab = ({ integrationId }: SubmissionsTabProps) => {
  const [statusFilter, setStatusFilter] = useState<KSeFSubmissionStatus | undefined>(undefined);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<number | null>(null);
  const { showSuccess, showError } = useToaster();

  const { isLoading, submissions, invalidate } = useKsefSubmissions(integrationId, 0, 50, statusFilter);

  const handleRetry = async (submissionId: number) => {
    try {
      await ksefApi.retrySubmission(integrationId, submissionId);
      showSuccess("Ponowna próba wysłania zakolejkowana");
      invalidate();
    } catch (e) {
      showError("Nie udało się zakolejkować ponownej próby");
    }
  };

  const handleDownloadUpo = async (submissionId: number) => {
    try {
      const response = await ksefApi.downloadUpo(integrationId, submissionId);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `upo-${submissionId}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      showError("Nie udało się pobrać UPO");
    }
  };

  const columns = React.useMemo<TanTableColumn<KSeFSubmissionDto>[]>(
    () => [
      { id: "id", accessorKey: "id", header: "ID", size: 60, enableColumnFilter: false },
      { id: "invoiceId", accessorKey: "invoiceId", header: "ID faktury", size: 90, enableColumnFilter: false },
      {
        id: "ksefNumber",
        accessorKey: "ksefNumber",
        header: "Numer KSeF",
        size: 200,
        enableColumnFilter: false,
        cell: (info) => info.getValue() ?? "—",
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        size: 150,
        enableColumnFilter: false,
        cell: (info) => {
          const s = info.getValue<KSeFSubmissionStatus>();
          return (
            <span className={`rounded px-2 py-0.5 text-xs font-medium ${statusColor[s]}`}>{statusLabel[s]}</span>
          );
        },
      },
      {
        id: "retryCount",
        accessorKey: "retryCount",
        header: "Próby",
        size: 70,
        enableColumnFilter: false,
      },
      {
        id: "submittedAt",
        accessorKey: "submittedAt",
        header: "Wysłano",
        size: 160,
        enableColumnFilter: false,
        cell: (info) => info.getValue<Date | undefined>()?.toLocaleString() ?? "—",
      },
      {
        id: "acceptedAt",
        accessorKey: "acceptedAt",
        header: "Zaakceptowano",
        size: 160,
        enableColumnFilter: false,
        cell: (info) => info.getValue<Date | undefined>()?.toLocaleString() ?? "—",
      },
      {
        id: "actions",
        header: "Akcje",
        size: 200,
        enableColumnFilter: false,
        cell: (info) => {
          const row = info.row.original;
          const canRetry = row.status === KSeFSubmissionStatus.Error || row.status === KSeFSubmissionStatus.Rejected;
          const canDownloadUpo = row.status === KSeFSubmissionStatus.Accepted;
          return (
            <div className="flex gap-1">
              <button
                onClick={() => setSelectedSubmissionId(row.id)}
                className="rounded bg-slate-100 px-2 py-1 text-xs hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600"
              >
                Szczegóły
              </button>
              {canRetry && (
                <button
                  onClick={() => handleRetry(row.id)}
                  className="rounded bg-orange-100 px-2 py-1 text-xs text-orange-700 hover:bg-orange-200"
                >
                  Ponów
                </button>
              )}
              {canDownloadUpo && (
                <button
                  onClick={() => handleDownloadUpo(row.id)}
                  className="rounded bg-green-100 px-2 py-1 text-xs text-green-700 hover:bg-green-200"
                >
                  UPO
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [],
  );

  if (isLoading) return <PaziaLoader position="center" />;

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        {statusFilters.map((f) => (
          <button
            key={f.label}
            onClick={() => setStatusFilter(f.value)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              statusFilter === f.value
                ? "bg-primary-600 text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <TanTableClient data={submissions} columns={columns} />

      <SubmissionDetailModal
        integrationId={integrationId}
        submissionId={selectedSubmissionId ?? 0}
        isOpen={selectedSubmissionId !== null}
        onClose={() => setSelectedSubmissionId(null)}
      />
    </div>
  );
};

const SubmissionDetailModal = ({
  integrationId,
  submissionId,
  isOpen,
  onClose,
}: {
  integrationId: number;
  submissionId: number;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { isLoading, submission } = useKsefSubmission(integrationId, submissionId);

  return (
    <PaziaModal isOpen={isOpen} onCloseRequest={onClose} id="submission-detail-modal" size="xl">
      <ModalPadding>
        <ModalHeader>Szczegóły zgłoszenia #{submissionId}</ModalHeader>
        {isLoading && <PaziaLoader position="center" />}
        {submission && <SubmissionDetail submission={submission} />}
      </ModalPadding>
    </PaziaModal>
  );
};

const SubmissionDetail = ({ submission }: { submission: KSeFSubmissionDetailDto }) => {
  const [showXml, setShowXml] = React.useState<"fa3" | "upo" | null>(null);
  const s = submission.status;

  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <span className="font-medium text-slate-500">ID</span>
        <span>{submission.id}</span>
        <span className="font-medium text-slate-500">ID faktury</span>
        <span>{submission.invoiceId}</span>
        <span className="font-medium text-slate-500">Status</span>
        <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${statusColor[s]}`}>
          {statusLabel[s]}
        </span>
        <span className="font-medium text-slate-500">Numer KSeF</span>
        <span>{submission.ksefNumber ?? "—"}</span>
        <span className="font-medium text-slate-500">Nr referencyjny</span>
        <span>{submission.ksefReferenceNumber ?? "—"}</span>
        <span className="font-medium text-slate-500">Wysłano</span>
        <span>{submission.submittedAt?.toLocaleString() ?? "—"}</span>
        <span className="font-medium text-slate-500">Zaakceptowano</span>
        <span>{submission.acceptedAt?.toLocaleString() ?? "—"}</span>
        <span className="font-medium text-slate-500">Próby / Maks</span>
        <span>
          {submission.retryCount} / {submission.maxRetries}
        </span>
        {submission.nextRetryAt && (
          <>
            <span className="font-medium text-slate-500">Następna próba</span>
            <span>{submission.nextRetryAt.toLocaleString()}</span>
          </>
        )}
        {submission.errorMessage && (
          <>
            <span className="font-medium text-slate-500">Błąd</span>
            <span className="text-red-600">{submission.errorMessage}</span>
          </>
        )}
        {submission.errorCode && (
          <>
            <span className="font-medium text-slate-500">Kod błędu</span>
            <span className="text-red-600">{submission.errorCode}</span>
          </>
        )}
      </div>
      <div className="flex gap-2">
        {submission.fa3Xml && (
          <button
            onClick={() => setShowXml(showXml === "fa3" ? null : "fa3")}
            className="rounded bg-slate-100 px-3 py-1 text-xs hover:bg-slate-200"
          >
            {showXml === "fa3" ? "Ukryj" : "Pokaż"} FA(3) XML
          </button>
        )}
        {submission.upoXml && (
          <button
            onClick={() => setShowXml(showXml === "upo" ? null : "upo")}
            className="rounded bg-green-100 px-3 py-1 text-xs text-green-700 hover:bg-green-200"
          >
            {showXml === "upo" ? "Ukryj" : "Pokaż"} UPO XML
          </button>
        )}
      </div>
      {showXml && (
        <pre className="max-h-64 overflow-auto rounded bg-slate-50 p-3 text-xs text-slate-700 dark:bg-slate-900 dark:text-slate-300">
          {showXml === "fa3" ? submission.fa3Xml : submission.upoXml}
        </pre>
      )}
    </div>
  );
};
